import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db, auth, storage } from '../../firebase';
import {
    collection, addDoc, getDocs, updateDoc, deleteDoc,
    doc, query, where, getDoc, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import Webcam from "react-webcam";

// MUI Components
import {
    Paper, Typography, TextField, Button, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Pagination, CircularProgress, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, MenuItem, Autocomplete, Tooltip, Alert
} from '@mui/material';
import {
    Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon,
    Add as AddIcon, Close as CloseIcon, CameraAlt as CameraIcon,
    Autorenew as RetakeIcon, Visibility as ViewIcon, VerifiedUser as VerifyIcon,
    Send as SendIcon
} from '@mui/icons-material';

const VisitorLogs = () => {
    const [logs, setLogs] = useState([]);
    const [allocatedStudents, setAllocatedStudents] = useState([]);
    const [schoolId, setSchoolId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [viewPhoto, setViewPhoto] = useState(null);

    // Form State
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [visitorName, setVisitorName] = useState('');
    const [mobile, setMobile] = useState('');
    const [altMobile, setAltMobile] = useState('');
    const [relation, setRelation] = useState('');
    const [comingReason, setComingReason] = useState('');
    const [address, setAddress] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [inTime, setInTime] = useState('');
    const [outTime, setOutTime] = useState('');

    // OTP State
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [userOtp, setUserOtp] = useState('');
    const [generatedOtp, setGeneratedOtp] = useState('');

    // Webcam State
    const webcamRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(null);
    const [showCamera, setShowCamera] = useState(false);

    // Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (userDoc.exists()) {
                    const sId = userDoc.data().schoolId;
                    setSchoolId(sId);
                    fetchInitialData(sId);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const fetchInitialData = async (sId) => {
        // Fetch Students from Room Allocations only
        const allocSnap = await getDocs(query(collection(db, "roomallocations"), where("schoolId", "==", sId)));
        const list = allocSnap.docs.map(doc => ({
            id: doc.data().studentId,
            label: `${doc.data().studentName}`.toUpperCase(),
            studentName: doc.data().studentName
        }));
        setAllocatedStudents(list);
        fetchLogs(sId);
    };

    const fetchLogs = async (sId) => {
        const q = query(collection(db, "visitorLogs"), where("schoolId", "==", sId));
        const snap = await getDocs(q);
        setLogs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    // --- OTP Logic ---
    const handleSendOtp = () => {
        if (mobile.length < 10) return alert("Enter valid mobile number");
        const code = Math.floor(100000 + Math.random() * 900000).toString();
        setGeneratedOtp(code);
        setOtpSent(true);
        alert(`DEBUG: OTP sent to ${mobile} is ${code}`); // Replace with real SMS API
    };

    const handleVerifyOtp = () => {
        if (userOtp === generatedOtp) {
            setOtpVerified(true);
            setOtpSent(false);
        } else {
            alert("Invalid OTP");
        }
    };

    const capture = useCallback(() => {
        const imageSrc = webcamRef.current.getScreenshot();
        setImgSrc(imageSrc);
        setShowCamera(false);
    }, [webcamRef]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!otpVerified && !editId) return alert("Please verify Mobile via OTP first");
        setSaving(true);

        try {
            let photoUrl = imgSrc || null;
            if (imgSrc && imgSrc.startsWith('data:image')) {
                const storageRef = ref(storage, `visitors/${schoolId}/${Date.now()}.jpg`);
                await uploadString(storageRef, imgSrc, 'data_url');
                photoUrl = await getDownloadURL(storageRef);
            }

            const payload = {
                studentId: selectedStudent.id,
                studentName: selectedStudent.studentName,
                visitorName, mobile, altMobile, relation, 
                comingReason, address, date, inTime, outTime,
                photoUrl, schoolId, updatedAt: serverTimestamp()
            };

            if (editId) {
                await updateDoc(doc(db, "visitorLogs", editId), payload);
            } else {
                await addDoc(collection(db, "visitorLogs"), { ...payload, createdAt: serverTimestamp() });
            }
            handleClose();
            fetchLogs(schoolId);
        } catch (err) { console.error(err); }
        setSaving(false);
    };

    const handleClose = () => {
        setOpen(false); setEditId(null); setSelectedStudent(null);
        setVisitorName(''); setMobile(''); setAltMobile(''); setRelation('');
        setComingReason(''); setAddress(''); setImgSrc(null); setShowCamera(false);
        setOtpSent(false); setOtpVerified(false); setUserOtp('');
    };

    const startEdit = (row) => {
        setEditId(row.id);
        const stu = allocatedStudents.find(s => s.id === row.studentId);
        setSelectedStudent(stu || { id: row.studentId, label: row.studentName, studentName: row.studentName });
        setVisitorName(row.visitorName);
        setMobile(row.mobile);
        setAltMobile(row.altMobile || '');
        setRelation(row.relation);
        setComingReason(row.comingReason);
        setAddress(row.address);
        setDate(row.date);
        setInTime(row.inTime);
        setOutTime(row.outTime);
        setImgSrc(row.photoUrl);
        setOtpVerified(true); // Skip OTP on edit
        setOpen(true);
    };

    const filtered = logs.filter(l => (l.visitorName?.toLowerCase() || "").includes(searchTerm.toLowerCase()));
    const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    return (
        <Grid  sx={{ mt: 1, justifyContent: 'center' }}>
            <Grid className="col-sm-12 mb-3" item xs={12} md={11}>
                <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
                    <Grid className="row" alignItems="center" sx={{ mb: 3 }}>
                        <Grid className="col-sm-6" item xs={6}><Typography variant="h5" fontWeight="bold" color="primary">Hostel Visitor Logs</Typography></Grid>
                        <Grid className="col-sm-6" item xs={6} sx={{ textAlign: 'right' }}>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Add Visitor</Button>
                        </Grid>
                    </Grid>

                    <TextField size="small" placeholder="Search visitor name..." fullWidth onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 3, maxWidth: 400 }} InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'gray' }} /> }} />

                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell><b>Sr.No</b></TableCell>
                                    <TableCell><b>Date</b></TableCell>
                                    <TableCell><b>Visitor Name</b></TableCell>
                                    <TableCell><b>Student (Resident)</b></TableCell>
                                    <TableCell><b>Mobile</b></TableCell>
                                    <TableCell align="right"><b>Actions</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginated.map((row, i) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{(page - 1) * itemsPerPage + i + 1}</TableCell>
                                        <TableCell>{row.date}</TableCell>
                                        <TableCell>{row.visitorName}</TableCell>
                                        <TableCell>{row.studentName}</TableCell>
                                        <TableCell>{row.mobile}</TableCell>
                                        <TableCell align="right">
                                            {row.photoUrl && <IconButton size="small" color="info" onClick={() => setViewPhoto(row.photoUrl)}><ViewIcon fontSize="small" /></IconButton>}
                                            <IconButton size="small" color="primary" onClick={() => startEdit(row)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" onClick={async () => { if(window.confirm("Delete?")) { await deleteDoc(doc(db, "visitorLogs", row.id)); fetchLogs(schoolId); }}}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination count={Math.ceil(filtered.length / itemsPerPage)} sx={{ mt: 2, display: 'flex', justifyContent: 'center' }} onChange={(e, v) => setPage(v)} />
                </Paper>
            </Grid>

            {/* Entry Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontWeight: 'bold', bgcolor: '#f9f9f9' }}>{editId ? 'Edit Visitor Log' : 'New Visitor Entry'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Grid className="row" spacing={2}>
                            {/* Student Selection (From Room Allocations) */}
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
                                <Autocomplete
                                    options={allocatedStudents}
                                    getOptionLabel={(option) => option.label || ""}
                                    value={selectedStudent}
                                    onChange={(e, val) => setSelectedStudent(val)}
                                    renderInput={(params) => <TextField {...params} label="Meeting Student (Resident)" size="small" required />}
                                />
                            </Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
                                <TextField fullWidth label="Visitor Name" size="small" required value={visitorName} onChange={(e) => setVisitorName(e.target.value)} />
                            </Grid>

                            {/* Mobile with OTP System */}
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                                <TextField 
                                    fullWidth label="Visitor Mobile" size="small" required value={mobile} 
                                    onChange={(e) => setMobile(e.target.value)} 
                                    disabled={otpVerified}
                                    InputProps={{
                                        endAdornment: !otpVerified && (
                                            <IconButton onClick={handleSendOtp} size="small" color="primary"><SendIcon fontSize="small" /></IconButton>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                                <TextField fullWidth label="Alt/Guardian Mobile" size="small" value={altMobile} onChange={(e) => setAltMobile(e.target.value)} />
                            </Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                                <TextField fullWidth label="Relation" size="small" placeholder="Father, Brother, etc." value={relation} onChange={(e) => setRelation(e.target.value)} />
                            </Grid>

                            {/* OTP Verification UI */}
                            {otpSent && !otpVerified && (
                                <Grid className="col-sm-4 mb-3" item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 1, p: 1, bgcolor: '#fff9c4', borderRadius: 1 }}>
                                        <TextField label="Enter OTP" size="small" value={userOtp} onChange={(e) => setUserOtp(e.target.value)} />
                                        <Button variant="contained" size="small" onClick={handleVerifyOtp}>Verify</Button>
                                    </Box>
                                </Grid>
                            )}
                            {otpVerified && <Grid className="col-sm-4 mb-3" item xs={12}><Alert severity="success" sx={{ py: 0 }}>Mobile Number Verified</Alert></Grid>}

                            <Grid className="col-sm-4 mb-3" item xs={12} sm={8}>
                                <TextField fullWidth label="Coming Reason" size="small" value={comingReason} onChange={(e) => setComingReason(e.target.value)} />
                            </Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                                <TextField fullWidth label="Visit Date" type="date" size="small" required value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
                                <TextField fullWidth label="Address" size="small" multiline rows={2} value={address} onChange={(e) => setAddress(e.target.value)} />
                            </Grid>
                            <Grid className="col-sm-4 mb-3" item xs={6} sm={3}>
                                <TextField fullWidth label="In Time" type="time" size="small" value={inTime} onChange={(e) => setInTime(e.target.value)} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid className="col-sm-4 mb-3" item xs={6} sm={3}>
                                <TextField fullWidth label="Out Time" type="time" size="small" value={outTime} onChange={(e) => setOutTime(e.target.value)} InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3, p: 2, border: '1px dashed #aaa', textAlign: 'center', borderRadius: 2 }}>
                            {showCamera ? (
                                <Box>
                                    <Webcam audio={false} ref={webcamRef} screenshotFormat="image/jpeg" width="100%" style={{ maxWidth: '300px', borderRadius: '8px' }} />
                                    <Box sx={{ mt: 1 }}><Button variant="contained" onClick={capture}>Capture Visitor Photo</Button></Box>
                                </Box>
                            ) : (
                                <Box>
                                    {imgSrc ? (
                                        <Box>
                                            <img src={imgSrc} alt="Visitor" style={{ width: '120px', borderRadius: '8px' }} />
                                            <Box sx={{ mt: 1 }}><Button size="small" startIcon={<RetakeIcon />} onClick={() => setShowCamera(true)}>Retake</Button></Box>
                                        </Box>
                                    ) : (
                                        <Button variant="outlined" startIcon={<CameraIcon />} onClick={() => setShowCamera(true)}>Capture Visitor Photo</Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={saving || (!otpVerified && !editId)}>
                            {saving ? 'Saving...' : 'Save Log'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Photo Viewer */}
            <Dialog open={!!viewPhoto} onClose={() => setViewPhoto(null)}>
                <DialogTitle>Visitor Photo</DialogTitle>
                <DialogContent><img src={viewPhoto} alt="Visitor" style={{ width: '100%', borderRadius: '8px' }} /></DialogContent>
                <DialogActions><Button onClick={() => setViewPhoto(null)}>Close</Button></DialogActions>
            </Dialog>
        </Grid>
    );
};

export default VisitorLogs;